import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { getPostLoginRedirectPath } from '../marketplace/didarSettingRoute';
import { useFormik } from 'formik';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { enqueueSnackbar } from 'notistack';

// OTP Input Component
interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error: boolean;
  helperText?: string;
  disabled?: boolean;
  shouldFocus?: boolean;
  onAutoSubmit?: () => void;
  onClearAndFocus?: (clearAndFocus: () => void) => void;
}

const OTPInput: React.FC<OTPInputProps> = ({ value, onChange, onBlur, error, helperText, disabled, shouldFocus, onAutoSubmit, onClearAndFocus }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isComposingRef = useRef<boolean>(false);
  
  // Create array with proper order - right to left (reverse order)
  const otpValues = Array(6).fill('');
  for (let i = 0; i < Math.min(value.length, 6); i++) {
    // Map the value to the reversed array (right to left)
    const reversedIndex = 5 - i;
    otpValues[reversedIndex] = value[i];
  }

  // Set initial focus on rightmost input (index 5) when component mounts or when shouldFocus changes
  useEffect(() => {
    if (shouldFocus && inputRefs.current[5] && !disabled) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        inputRefs.current[5]?.focus();
      }, 100);
    }
  }, [shouldFocus, disabled]);

  // Function to clear all OTP boxes and focus on the first one
  const clearAndFocus = () => {
    onChange(''); // Clear the OTP value
    // Focus on the rightmost input (index 5) which is the first one in our right-to-left flow
    setTimeout(() => {
      inputRefs.current[5]?.focus();
    }, 100);
  };

  // Expose the clearAndFocus function to parent component
  useEffect(() => {
    if (onClearAndFocus) {
      onClearAndFocus(clearAndFocus);
    }
  }, [onClearAndFocus]);

  // Convert Persian/Farsi digits to English digits
  const convertToEnglishDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    let result = str;
    
    // Convert Persian digits
    persianDigits.forEach((persian, index) => {
      result = result.replace(new RegExp(persian, 'g'), index.toString());
    });
    
    // Convert Arabic digits
    arabicDigits.forEach((arabic, index) => {
      result = result.replace(new RegExp(arabic, 'g'), index.toString());
    });
    
    return result;
  };

  const handleInputChange = (displayIndex: number, inputValue: string) => {
    // Convert Persian/Arabic digits to English digits
    inputValue = convertToEnglishDigits(inputValue);
    
    // Only allow single digit
    if (inputValue.length > 1) {
      inputValue = inputValue.slice(-1);
    }
    
    // Only allow digits
    if (!/^\d*$/.test(inputValue)) {
      return;
    }

    const newOtpValues = [...otpValues];
    newOtpValues[displayIndex] = inputValue;
    
    // Create the actual OTP string in correct order (left to right)
    const actualOtpArray = Array(6).fill('');
    for (let i = 0; i < 6; i++) {
      actualOtpArray[i] = newOtpValues[5 - i];
    }
    const newOtpCode = actualOtpArray.join('');
    
    onChange(newOtpCode);

    // Auto submit when all 6 digits are entered
    if (newOtpCode.length === 6 && onAutoSubmit) {
      // Small delay to ensure the UI updates before submitting
      setTimeout(() => {
        onAutoSubmit();
      }, 100);
    }

    // Auto focus to next input (left direction)
    if (inputValue && displayIndex > 0) {
      inputRefs.current[displayIndex - 1]?.focus();
    }
  };

  const handleKeyDown = (displayIndex: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otpValues[displayIndex] && displayIndex < 5) {
        // If current box is empty, move to next box (right direction)
        inputRefs.current[displayIndex + 1]?.focus();
      } else if (otpValues[displayIndex]) {
        // If current box has value, clear it
        const newOtpValues = [...otpValues];
        newOtpValues[displayIndex] = '';
        
        // Create the actual OTP string in correct order (left to right)
        const actualOtpArray = Array(6).fill('');
        for (let i = 0; i < 6; i++) {
          actualOtpArray[i] = newOtpValues[5 - i];
        }
        const newOtpCode = actualOtpArray.join('');
        onChange(newOtpCode);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      onChange(pastedData);
      
      // Auto submit when all 6 digits are pasted
      if (pastedData.length === 6 && onAutoSubmit) {
        // Small delay to ensure the UI updates before submitting
        setTimeout(() => {
          onAutoSubmit();
        }, 100);
      }
      
      // Focus the rightmost empty input or the rightmost input
      const nextIndex = Math.max(5 - pastedData.length + 1, 0);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleBlur = () => {
    onBlur();
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (displayIndex: number, e: React.CompositionEvent) => {
    isComposingRef.current = false;
    const target = e.currentTarget as HTMLInputElement;
    if (target && target.value) {
      handleInputChange(displayIndex, target.value);
    }
  };

  const handleInput = (displayIndex: number, e: React.FormEvent) => {
    // Use onInput for Safari compatibility, especially with Persian keyboard
    if (!isComposingRef.current) {
      const target = e.currentTarget as HTMLInputElement;
      if (target && target.value) {
        handleInputChange(displayIndex, target.value);
      }
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'center', mb: 1 }}>
        {Array.from({ length: 6 }, (_, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            inputProps={{
              maxLength: 1,
              type: 'tel',
              inputMode: 'numeric',
              pattern: '[0-9]*',
              lang: 'en',
              style: {
                textAlign: 'center',
                fontWeight: 'bold',
              },
            }}
            value={otpValues[index]}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onInput={(e) => handleInput(index, e)}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={(e) => handleCompositionEnd(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onBlur={handleBlur}
            error={error}
            disabled={disabled}
            sx={{
              width: { xs: '55px', sm: '60px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fff',
                height: { xs: '55px', sm: '60px' },
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '1.5rem', sm: '1.8rem' },
                padding: { xs: '12px', sm: '16px' },
              },
            }}
          />
        ))}
      </Box>
      {error && helperText && (
        <Typography variant="caption" color="error" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

interface LoginForm {
  identifier: string;
  otpCode: string;
  languagePreference: string;
}

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithEmail, signup, signupWithEmail, sendOTP, sendSigninEmailRequest, sendSignupEmailRequest, loading } = useAuth();
  const { t, currentLanguage, setLanguage } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [inputType, setInputType] = useState<'phone' | 'email' | null>(null);
  const [searchParams] = useSearchParams();
  const clearOtpAndFocusRef = useRef<(() => void) | null>(null);

  // Get UTM parameters from URL
  const utm_source = searchParams.get('utm_source') || '';
  const utm_medium = searchParams.get('utm_medium') || '';
  const utm_campaign = searchParams.get('utm_campaign') || '';
  const utm_term = searchParams.get('utm_term') || '';
  const utm_content = searchParams.get('utm_content') || '';

  // Function to set cookie
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  };

  // Check for 'r' parameter and save to cookie
  useEffect(() => {
    const rParam = searchParams.get('r');
    if (rParam) {
      // Save to cookie for 30 days (1 month)
      setCookie('r', rParam, 30);
    }
  }, [searchParams]);

  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang) {
      setLanguage(lang);
    } else {
      // Set language based on domain if no lang parameter is provided
      const domain = window.location.hostname;
      if (domain === 'app.aidasales.net') {
        setLanguage('en');
      } else if (domain === 'app.aidasales.ir') {
        setLanguage('fa');
      }
    }
  }, [searchParams, setLanguage]);

  // Sync formik's languagePreference with currentLanguage whenever it changes
  useEffect(() => {
    formik.setFieldValue('languagePreference', currentLanguage);
  }, [currentLanguage]);

  const formik = useFormik<LoginForm>({
    initialValues: {
      identifier: '',
      otpCode: '',
      languagePreference: currentLanguage,
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (values) => {
      const errors: any = {};
      
      if (activeStep === 0) {
        // Validate identifier
        if (!values.identifier || values.identifier.length === 0) {
          errors.identifier = currentLanguage === 'fa' ? 'لطفا شماره تلفن یا ایمیل خود را وارد کنید' : 'Please enter your phone number or email';
        } else if (inputType === 'email') {
          // Validate as email only if inputType is email
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(values.identifier)) {
            errors.identifier = currentLanguage === 'fa' ? 'ایمیل نامعتبر است' : 'Invalid email';
          }
        } else if (inputType === 'phone') {
          // Validate as phone only if inputType is phone
          const phoneRegex = /^\+?[0-9۰-۹]+$/;
          if (!phoneRegex.test(values.identifier)) {
            errors.identifier = currentLanguage === 'fa' ? 'شماره تلفن نامعتبر است' : 'Invalid phone number';
          }
        }
      } else {
        // Validate OTP
        if (!values.otpCode) {
          errors.otpCode = currentLanguage === 'fa' ? 'لطفا کد تایید را وارد کنید' : 'Please enter OTP code';
        } else if (values.otpCode.length !== 6) {
          errors.otpCode = currentLanguage === 'fa' ? 'کد تایید باید ۶ رقم باشد' : 'OTP must be 6 digits';
        }
      }
      
      return errors;
    },
    onSubmit: async (values) => {
      try {
        if (activeStep === 0) {
          // Step 1: Try to signin first
          const isEmail = values.identifier.includes('@');
          
          try {
            // Try signin
            if (isEmail) {
              await sendSigninEmailRequest(values.identifier);
            } else {
              await sendOTP(values.identifier, 'signin', values.languagePreference);
            }
            setIsSignupMode(false);
            setActiveStep(1);
          } catch (signinError: any) {
            const detail = signinError?.response?.data?.detail || '';
            
            // Check if user not found errors
            if (
              detail.includes("User with this phone number doesn't exist") ||
              detail.includes("User with this email doesn't exist") ||
              detail.includes("کاربری با این ایمیل یافت نشد") ||
              detail.includes("User not found") ||
              detail.includes("doesn't exist") ||
              detail.includes("خطا در ارسال کد تایید: ")
            ) {
              // Show info message and switch to signup mode
              enqueueSnackbar(
                currentLanguage === 'fa' 
                  ? 'به دلیل ثبت‌ نام نبودن شما، در حال ثبت‌ نام هستید'
                  : 'You are not registered. Signing you up now',
                { 
                  variant: 'info',
                  autoHideDuration: 3000,
                }
              );
              
              // Now try signup
              if (isEmail) {
                await sendSignupEmailRequest(values.identifier, values.languagePreference, utm_source, utm_medium, utm_campaign, utm_term, utm_content);
              } else {
                await sendOTP(values.identifier, 'signup', values.languagePreference, utm_source, utm_medium, utm_campaign, utm_term, utm_content);
              }
              setIsSignupMode(true);
              setActiveStep(1);
            } else {
              // Other errors, throw them to outer catch
              throw signinError;
            }
          }
        } else {
          // Step 2: Verify OTP
          const isEmail = values.identifier.includes('@');
          let authenticatedUser;

          if (isSignupMode) {
            // Signup OTP verification
            if (isEmail) {
              authenticatedUser = await signupWithEmail(values.identifier, values.otpCode);
            } else {
              authenticatedUser = await signup(values.identifier, values.otpCode);
            }
          } else {
            // Signin OTP verification
            if (isEmail) {
              authenticatedUser = await loginWithEmail(values.identifier, values.otpCode);
            } else {
              authenticatedUser = await login(values.identifier, values.otpCode);
            }
          }

          navigate(
            getPostLoginRedirectPath(searchParams, location, authenticatedUser?.membership),
            { replace: true },
          );
        }
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        
        // Handle other error cases
        if (status === 422) {
          enqueueSnackbar(t('auth.login.validationError'), { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else if (status === 429) {
          enqueueSnackbar(t('auth.login.tooManyRequests'), { 
            variant: 'error',
            autoHideDuration: 5000,
          });
        } else if (status === 500) {
          let message = t('auth.login.serverError');
          if (detail && detail.includes("Invalid phone number format")) {
            message = t('auth.errors.invalidPhoneNumber');
          } else if (detail && detail.includes("خطا در ورود ایمیل")) {
            message = t('auth.errors.waitRequest');
          } else if (detail && detail.includes("Please wait")) {
            message = t('auth.errors.waitRequest');
          } else if (detail && detail.includes("کد تایید نامعتبر است") || detail && detail.includes("Invalid OTP")) {
            message = t('auth.errors.invalidOtp');
            // Clear OTP and focus on first input when OTP is invalid
            if (clearOtpAndFocusRef.current) {
              clearOtpAndFocusRef.current();
            }
          } else if (detail && detail.includes("آدرس ایمیل قبلاً ثبت شده است")) {
            message = t('auth.errors.userAlreadyExists');
          } else if (detail && detail.includes("تلفن همراه از قبل ثبت نام شده")) {
            message = t('auth.errors.userAlreadyExists');
          } else {
            message = detail || t('auth.errors.serverError');
          }
          enqueueSnackbar(message, { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else if (status === 503) {
          enqueueSnackbar(t('auth.login.serviceUnavailable'), { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else if (status === 0 || !status) {
          enqueueSnackbar(t('auth.login.networkError'), { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else {
          enqueueSnackbar(detail || t('auth.login.genericError'), { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        }
      }
    },
  });


  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    formik.handleChange(e);
    
    // Clear errors when user starts typing
    if (formik.errors.identifier) {
      formik.setErrors({ ...formik.errors, identifier: undefined });
    }
    
    // Detect input type based on first character
    if (value.length > 0) {
      const firstChar = value.charAt(0);
      // Check for English digits (0-9), Persian digits (۰۱۲۳۴۵۶۷۸۹), or + sign
      if (/[0-9۰-۹+]/.test(firstChar)) {
        // First character is a number (English or Persian) or +, so it's a phone number
        setInputType('phone');
      } else if (/[a-zA-Z]/.test(firstChar)) {
        // First character is a letter, so it's an email
        setInputType('email');
      }
    } else {
      // Reset to null when field is empty
      setInputType(null);
    }
  };

  const getLabel = () => {
    const value = formik.values.identifier;
    if (!value || value.trim() === '') {
      return currentLanguage === 'fa' ? 'شماره تماس / ایمیل' : 'Phone Number / Email';
    }
    
    // بررسی اینکه آیا حرف یا کاراکتر خاص (مثل @) وجود دارد
    const hasLetter = /[a-zA-Z@]/.test(value);
    
    if (hasLetter) {
      return currentLanguage === 'fa' ? 'ایمیل' : 'Email';
    } else {
      return currentLanguage === 'fa' ? 'شماره تماس' : 'Phone Number';
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' ,background: 'linear-gradient(152deg,rgba(103, 240, 208, 1) 18%, rgba(255, 255, 255, 1) 100%)'}}>
        <Box sx={{ width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', gap:'40px', padding: '20px' }}>
            <Box
                component="img"
                src="/images/aida-logo-ok.svg"
                alt=""
                sx={{
                    width: { xs: 85, sm: 100 },
                }}
            />
            <Box
                component="p"
                sx={{
                    margin: 0,
                    padding: 0,
                    fontSize: { xs: 20, sm: 30 },
                    fontWeight: 500,
                    color: '#000',
                }}
            >
                {currentLanguage === 'fa' ? 'دختری ساخته شده با هوش مصنوعی' : 'AI-Powered Assistant'}
            </Box>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap:'15px' }}>
                <form onSubmit={formik.handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {activeStep === 0 ? (
                    <TextField
                        id="identifier"
                        name="identifier"
                        label={getLabel()}
                        placeholder={
                            inputType === 'email'
                              ? (currentLanguage === 'fa' ? 'ایمیل خود را وارد کنید' : 'Enter your email')
                              : inputType === 'phone'
                              ? (currentLanguage === 'fa' ? 'شماره تلفن همراه خود را وارد کنید' : 'Enter your phone number')
                              : (currentLanguage === 'fa' ? '09********* / ***@gmail.com' : '09********* / ***@gmail.com')
                        }
                        fullWidth
                        value={formik.values.identifier}
                        onChange={handleIdentifierChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.identifier && Boolean(formik.errors.identifier)}
                        helperText={formik.touched.identifier && formik.errors.identifier}
                        disabled={loading}
                        InputLabelProps={{
                            sx: {
                                fontSize: 16,
                                color: '#000',
                                textAlign: 'right',
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                overflow: 'hidden',
                                height: '55px',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                '& .MuiInputBase-input': {
                                    fontSize: 16,
                                    color: '#000',
                                    padding: '0 15px',
                                    direction: 'rtl',
                                    textAlign: 'right',
                                    backgroundColor: '#fff',
                                    height: '100%',
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                margin: '4px 0 0 0',
                                textAlign: 'right',
                            }
                        }}
                    />
                  ) : (
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: '#000', fontSize: 16 }}>
                        {t('auth.login.otpLabel')}
                      </Typography>
                      <OTPInput
                        value={formik.values.otpCode}
                        onChange={(value) => formik.setFieldValue('otpCode', value)}
                        onBlur={() => formik.setFieldTouched('otpCode', true)}
                        error={Boolean(formik.touched.otpCode && formik.errors.otpCode)}
                        helperText={formik.touched.otpCode && formik.errors.otpCode ? String(formik.errors.otpCode) : undefined}
                        disabled={loading}
                        shouldFocus={activeStep === 1}
                        onAutoSubmit={() => formik.handleSubmit()}
                        onClearAndFocus={(clearAndFocus) => {
                          clearOtpAndFocusRef.current = clearAndFocus;
                        }}
                      />
                    </Box>
                  )}

                  {activeStep === 0 && (
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={loading || !formik.values.identifier}
                      sx={{
                        backgroundColor:'#00AF9E',
                        color:'#fff',
                        fontSize: 20,
                        fontWeight: 600,
                        borderRadius: '10px',
                        height: '55px',
                        boxShadow:'none',
                        "&:hover": {
                          backgroundColor:'#00AF9E'
                        },
                        "&:disabled": {
                          backgroundColor: '#cccccc',
                          color: '#666666'
                        }
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        currentLanguage === 'fa' ? 'ورود یا ثبت نام به آیدا' : 'Enter Aida'
                      )}
                    </Button>
                  )}

                  {/* {activeStep === 10 && (
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Button
                          variant={currentLanguage === 'fa' ? 'contained' : 'outlined'}
                          onClick={() => {
                            setLanguage('fa');
                            formik.setFieldValue('languagePreference', 'fa');
                          }}
                          sx={{ 
                            minWidth: 100,
                            borderRadius: '8px',
                          }}
                        >
                          {t('auth.language.fa')}
                        </Button>
                        <Button
                          variant={currentLanguage === 'en' ? 'contained' : 'outlined'}
                          onClick={() => {
                            setLanguage('en');
                            formik.setFieldValue('languagePreference', 'en');
                          }}
                          sx={{ 
                            minWidth: 100,
                            borderRadius: '8px',
                          }}
                        >
                          {t('auth.language.en')}
                        </Button>
                      </Box>
                    </Box>
                  )} */}
                </form>
            </Box>
        </Box>
    </Box>
  );
};

export default Auth; 